import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@react/react-spectrum/Button';
import Checkbox from '@react/react-spectrum/Checkbox';
import Dialog from '@react/react-spectrum/Dialog';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import Link from '@react/react-spectrum/Link';
import Textfield from '@react/react-spectrum/Textfield';
import Wait from '@react/react-spectrum/Wait';

import CheckmarkCircle from '@react/react-spectrum/Icon/CheckmarkCircle';
import CloseCircle from '@react/react-spectrum/Icon/CloseCircle';
import Alert from '@react/react-spectrum/Icon/Alert';
import LinkOut from '@react/react-spectrum/Icon/LinkOut';

import DealRegistrationInput from 'components/DealRegistrationInput';
import { FeaturesContext } from 'features/FeaturesContext';

import { QuickSetupContext } from './Context';
import { startJob, STATUS } from 'jobs/jobManager';
import { PATHS } from 'config/constants';
import AdditionalParamaters from './AdditionalParamaters';
import WorkspaceParameters from './WorkspaceParameters';

import { useCurrentUser } from 'root/hooks';

import A from 'services/analytics';
import { useDsnAxios } from 'services/axios';
import { API_ROOT, getHomeUrl } from 'services/environment';
import { fetchImsTokenExpiry, reauthenticate } from 'services/imsTokenExpiry';

import {
  LIVEDEMOS_STATUS,
  useLivedemosStep,
  LivedemosProgress,
  isLivedemosStepEnabled,
  showLivedemosOptIn
} from './livedemos';

// Minimum IMS token lifetime required to start Quick Setup, in seconds.
// Quick Setup runs jobs sequentially and finishes with a livedemos call
// authorised by the IMS token; if the token expires mid-run that final
// step fails after all the work is done. Quick Setup typically completes
// in 2–5 minutes, so 20 minutes leaves a comfortable margin while still
// being short enough that a freshly-logged-in user (24h cookie) is never
// prompted.
const QUICK_SETUP_MIN_IMS_LIFETIME_SECONDS = 20 * 60;

async function checkImsTokenForLivedemos() {
  const expiry = await fetchImsTokenExpiry();
  if (expiry?.pathological) {
    // eslint-disable-next-line no-console
    console.warn(
      'IMS token has a pathologically short TTL — livedemos registration will likely fail. Re-authenticating would not help; ask an admin to review the IMS client TTL configuration.'
    );
    return null;
  }
  if (
    expiry &&
    expiry.secondsRemaining !== null &&
    expiry.secondsRemaining < QUICK_SETUP_MIN_IMS_LIFETIME_SECONDS
  ) {
    return {
      secondsRemaining: expiry.secondsRemaining,
      reauthInProgress: false
    };
  }
  return null;
}

export default function Actions() {
  const history = useHistory();
  const { isFeatureEnabled, QUICK_SETUP_INITIAL_VALIDATION } =
    useContext(FeaturesContext);
  const { context } = useContext(QuickSetupContext);
  const {
    environment,
    configuration,
    selectedValues,
    title,
    workspaceId,
    existingLivedemosWorkspaceId,
    drNumber,
    additionalParams
  } = context;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  // Re-auth pre-flight state. `reauthState` is null when no prompt is needed,
  // otherwise carries `{ secondsRemaining, reauthInProgress }` for the dialog.
  const [reauthState, setReauthState] = useState(null);
  const [startCheckInProgress, setStartCheckInProgress] = useState(false);

  // Live Demos step runs when a preset is selected, a DSN workspace is used, and the
  // user opted in (default). Reusing a DSN workspace that already has a livedemos
  // link always registers the demo regardless of the checkbox.
  const livedemosEnabled = isLivedemosStepEnabled(context);

  const livedemos = useLivedemosStep({
    enabled: livedemosEnabled,
    title,
    drNumber,
    presetName: context.preset?.name
  });

  const openJobsDialog = () => {
    // only consider jobs for which users selected any parameters, others will be skipped
    // always include initial-validation
    const jobsWithParams = context.jobs.filter((job) => {
      const jobParams = selectedValues[job.path];
      return (
        jobParams?.variants?.length > 0 ||
        jobParams?.channels?.length > 0 ||
        job.name === 'initial-validation' ||
        job.name === 'post-processing'
      );
    });

    // if quick-setup/initial-validation flag is not set, remove initial-validation job
    if (!isFeatureEnabled(QUICK_SETUP_INITIAL_VALIDATION)) {
      jobsWithParams.shift();
    }

    setJobs(jobsWithParams);

    A.triggerQuickSetupStart();
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    if (jobsStarted()) {
      return;
    }

    // IMS pre-flight only when the Live Demos step will run (respects opt-in checkbox).
    if (isLivedemosStepEnabled(context)) {
      setStartCheckInProgress(true);
      try {
        const reauthNeeded = await checkImsTokenForLivedemos();
        if (reauthNeeded) {
          setReauthState(reauthNeeded);
          return;
        }
      } finally {
        setStartCheckInProgress(false);
      }
    }

    nextJob(0);
  };

  const handleReauthenticate = async () => {
    setReauthState((s) => (s ? { ...s, reauthInProgress: true } : s));
    await reauthenticate();
  };

  // TODO: move job orchestration to a separate component
  const nextJob = async (i) => {
    try {
      if (i >= jobs.length) {
        // setup livedemos workspace after all jobs are completed - if enabled
        if (livedemosEnabled) {
          const dsnWorkspaceId = jobs.find((j) => j.name === 'post-processing')
            ?.result?.results?.[0]?.id;
          livedemos.run(dsnWorkspaceId, existingLivedemosWorkspaceId);
        }
        return;
      }

      const job = jobs[i];
      const jobParams = selectedValues[job.path];
      if (
        (Array.isArray(jobParams?.variants) &&
          jobParams?.variants.length > 0) ||
        (Array.isArray(jobParams?.channels) &&
          jobParams?.channels.length > 0) ||
        job.name === 'initial-validation' ||
        job.name === 'post-processing'
      ) {
        jobs[i] = {
          ...job,
          status: STATUS.IN_PROGRESS
        };
        setJobs([...jobs]);

        // job context lets current job access results of previous jobs
        const jobContext = jobs.reduce((accumulator, job) => {
          accumulator[job.path] =
            job.status === STATUS.SUCCESS ||
            job.status === STATUS.SUCCESS_WITH_ERRORS
              ? (job.result?.detailedResults ?? job.result?.results)
              : {};

          return accumulator;
        }, {});

        A.triggerQuickSetupJob(job.title, job.name);
        const result = await startJob({
          name: job.name,
          params: {
            ...job.params,
            context: jobContext,
            title,
            workspaceId,
            drNumber,
            environment,
            configuration,
            values: selectedValues,
            additionalParams: additionalParams
          },
          wait: true,
          pollingInterval: job.pollingInterval
        });

        jobs[i] = {
          ...job,
          ...result
        };

        // if validation job returns an error, abort remaining jobs
        if (
          job.name === 'initial-validation' &&
          result.status === STATUS.ERROR
        ) {
          abortRemainingJobs(jobs, i);
          if (livedemosEnabled) livedemos.abort();
          return;
        }

        // TODO: let the user decide if remaining jobs should be aborted
      } else {
        jobs[i] = {
          ...job,
          status: STATUS.SKIPPED,
          result: {
            message: 'No parameters selected, job skipped'
          }
        };
      }

      setJobs([...jobs]);
      nextJob(++i);
    } catch (error) {
      const job = jobs[i];

      jobs[i] = {
        ...job,
        status: STATUS.ERROR,
        error: {
          message: `Couldn't start job ${job.name}, please contact us at demosystem@adobe.com`,
          details: [error?.response?.data?.message || error?.message]
        }
      };
      setJobs([...jobs]);

      abortRemainingJobs(jobs, i);
      if (livedemosEnabled) livedemos.abort();
    }
  };

  const abortRemainingJobs = (jobs, i) => {
    for (let j = i + 1; j < jobs.length; j++) {
      jobs[j] = {
        ...jobs[j],
        status: STATUS.ABORTED,
        error: { message: 'Job aborted because initial validation failed' }
      };
    }

    setJobs([...jobs]);
  };

  const jobsStarted = () => {
    return jobs.filter((job) => job.status).length > 0;
  };

  const jobsFinished = () => {
    return jobs.filter((job) => job.status).length === jobs.length;
  };

  return (
    <div className="actions">
      <Button onClick={() => history.push(PATHS.HOME)}>Cancel</Button>

      <Button
        variant="cta"
        disabled={!environment || !configuration}
        onClick={openJobsDialog}>
        Start
      </Button>

      {reauthState && (
        <ReauthDialog
          secondsRemaining={reauthState.secondsRemaining}
          reauthInProgress={reauthState.reauthInProgress}
          onConfirm={handleReauthenticate}
          onCancel={() => setReauthState(null)}
        />
      )}

      {dialogOpen && (
        <div className="jobs-dialog spectrum-Underlay is-open">
          <Dialog
            confirmDisabled={!title || jobsStarted() || startCheckInProgress}
            confirmLabel="Start"
            cancelLabel={
              (
                livedemosEnabled
                  ? jobsFinished() && livedemos.isSettled
                  : jobsFinished()
              )
                ? 'Close'
                : 'Cancel'
            }
            onConfirm={() => {
              void handleDialogConfirm();
            }}
            onCancel={() => {
              setDialogOpen(false);
              livedemos.reset();
            }}>
            {!jobsStarted() && <ParametersForm />}
            {jobsStarted() && (
              <>
                <JobsProgress jobs={jobs} />
                {/* if initial validation failed, livedemos step is aborted - it disappears from the UI */}
                {livedemosEnabled &&
                  livedemos.status !== LIVEDEMOS_STATUS.ABORTED && (
                    <>
                      <div
                        className="jobs-progress-divider"
                        role="separator"
                        aria-orientation="horizontal"
                      />
                      <LivedemosProgress
                        status={livedemos.status}
                        workspaceId={livedemos.workspaceId}
                        error={livedemos.error}
                        reusedExistingWorkspace={
                          livedemos.reusedExistingWorkspace
                        }
                      />
                    </>
                  )}
              </>
            )}
          </Dialog>
        </div>
      )}
    </div>
  );
}

function ReauthDialog({
  secondsRemaining,
  reauthInProgress,
  onConfirm,
  onCancel
}) {
  const minutes = Math.max(1, Math.round(secondsRemaining / 60));
  return (
    <div className="jobs-dialog spectrum-Underlay is-open">
      <Dialog
        title="Re-authenticate to continue"
        confirmLabel={reauthInProgress ? 'Redirecting…' : 'Re-authenticate'}
        cancelLabel="Cancel"
        confirmDisabled={reauthInProgress}
        onConfirm={onConfirm}
        onCancel={onCancel}>
        <p>
          Your session expires in about {minutes} minute
          {minutes === 1 ? '' : 's'}, which may be too short for Quick Setup to
          finish. The final livedemos registration step would fail after all
          jobs have already run.
        </p>
        <p>
          Please re-authenticate now. You'll be redirected to sign in and
          returned to this page.
        </p>
      </Dialog>
    </div>
  );
}

function ParametersForm() {
  const { context, setContextValue } = useContext(QuickSetupContext);

  return (
    <form className="parameters-form">
      <FieldLabel label="Title" necessity="required" labelFor="title-input">
        <Textfield
          id="title-input"
          defaultValue={context.title}
          onChange={(value) => setContextValue({ path: 'title', value })}
        />
        <div className="field-description">
          <em>
            This value will be used as a name of Data Collection property and
            Experience Builder projects.
          </em>
        </div>
      </FieldLabel>

      <WorkspaceParameters />

      <AdditionalParamaters />

      {showLivedemosOptIn(context) && (
        <div>
          <Checkbox
            checked={context.registerInLivedemos !== false}
            onChange={() =>
              setContextValue({
                path: 'registerInLivedemos',
                value: context.registerInLivedemos === false
              })
            }>
            Register in Live Demos
          </Checkbox>
          <div className="field-description">
            <em>
              Create a Live Demos workspace and link this DSN setup for assets,
              branding, and product scraping. Uncheck for training or internal
              setups that only need DSN.
            </em>
          </div>
        </div>
      )}

      <DealRegistrationInput
        defaultValue={context.drNumber}
        onChange={(value) => setContextValue({ path: 'drNumber', value })}
      />
    </form>
  );
}

function JobsProgress({ jobs }) {
  return (
    <div className="jobs-progress">
      {jobs.map((job, index) => (
        <div key={`${job.name}-${index}`} className="job">
          <div className="overview">
            <span className="job-icon">
              {job.status === STATUS.IN_PROGRESS && <Wait size="S" />}
              {job.status === STATUS.SUCCESS && (
                <CheckmarkCircle className="success" size="S" />
              )}
              {job.status === STATUS.SUCCESS_WITH_ERRORS && (
                <Alert className="success-with-errors" size="S" />
              )}
              {job.status === STATUS.ERROR && (
                <CloseCircle className="error" size="S" />
              )}
              {(job.status === STATUS.ABORTED ||
                job.status === STATUS.SKIPPED) && (
                <CloseCircle className="aborted" size="S" />
              )}
            </span>

            <span className="job-title">{job.title}</span>
          </div>

          {job.status && job.status !== STATUS.IN_PROGRESS && (
            <JobDetails job={job} />
          )}
        </div>
      ))}
    </div>
  );
}

function JobDetails({ job }) {
  const { result, error } = job;
  const message =
    result?.message || error?.message || 'An unkown error occured';
  const details = result?.details || error?.details;
  const formattedMessage = result?.formattedMessage || error?.formattedMessage;

  const [show, setShow] = useState(!!error);
  const logsLink = `${API_ROOT}/jobs/${job.id}/logs?format=text`;

  return (
    <div className="job-details">
      {message && <div>{message}</div>}
      {formattedMessage && (
        <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />
      )}
      {Array.isArray(result?.links) && (
        <div className="links">
          {result?.links.map((link) => (
            <a
              href={link.url}
              key={link.url}
              className="spectrum-Link"
              target="_blank"
              rel="noreferrer noopener">
              {link.label} <LinkOut size="XS" />
            </a>
          ))}
        </div>
      )}

      <div className="actions">
        {Array.isArray(details) && details.length > 0 && (
          <Link onClick={() => setShow(!show)}>
            {show ? 'Hide details' : 'Show details'}
          </Link>
        )}

        {/* if job was completed with any result, show a link to logs */}
        {job.id && (
          <a
            href={logsLink}
            className="spectrum-Link"
            target="_blank"
            rel="noreferrer noopener">
            View logs
          </a>
        )}

        {/* if there was an error, show a button to report it */}
        {error && job.id && (
          <ReportError content={error} job={job} logsLink={logsLink} />
        )}
      </div>

      {Array.isArray(details) && details.length > 0 && show && (
        <div className="content">
          {details.map((detail) => (
            <div key={detail}>{detail}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportError({ content, job, logsLink }) {
  const currentUser = useCurrentUser();
  const userId = currentUser?.email?.toLowerCase()?.endsWith('@adobe.com')
    ? currentUser.id
    : currentUser.email;

  const [{ data, loading, error }, send] = useDsnAxios(
    {
      url: '/notifications/slack',
      method: 'POST',
      data: {
        source: 'quick-setup',
        content: `${userId} encountered an error in Quick Setup when running job *${
          job.name
        }*:\n>${content.message}\nDetails:\n>${
          content.details
        }\n<${getHomeUrl()}${logsLink}|Logs>`
      }
    },
    { manual: true }
  );

  if (loading) {
    return <Wait size="S" />;
  }

  if (error) {
    return (
      <div>
        We're sorry but an error occured when sending report: ${error.message}
      </div>
    );
  }

  if (data?.status === 'success') {
    return <div>Thank you, your error report was received</div>;
  }

  return <Link onClick={send}>Report error</Link>;
}
